import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';

import { mentionType } from '@aline/common';
import { ButtonBase, IconButton, Skeleton, Typography } from '@mui/material';
import { MentionInputPlugin, MentionPlugin } from '@platejs/mention/react';
import type { LucideIcon } from 'lucide-react';
import {
  ArrowLeft,
  BookOpenText,
  ChartColumnBig,
  ChevronRight,
  ClipboardList,
  FileText,
  Plus,
  Sparkle,
  Sparkles,
  Upload,
  UserRound,
  Workflow,
} from 'lucide-react';
import type { TElement } from 'platejs';
import type { PlateElementProps } from 'platejs/react';
import { toPlatePlugin, useEditorRef, useEditorSelector } from 'platejs/react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { AlineAvatar, alineAvatarSizes, AlineCheckbox, StorableIcon } from 'src/components';
import { UploadFilesPicker } from 'src/components/FilestackPicker';
import { Apollo } from 'src/gql/generated';
import { aiChatAcceptedMimeTypes, FileStackDomContainer } from 'src/helpers/integrations/filestack';
import { makeStyles } from 'tss-react/mui';
import { useDebounce } from 'use-debounce';

import { BaseMentionElementPlugin } from './mention-element-plugin';
import {
  DEFAULT_ENABLED_TYPES,
  filterUserResults,
  getMentionEntitySearchCursorInput,
  getMultiSelectResultForItem,
  getPickerItems,
  getSearchMentionEntitiesInput,
  getSearchResultDisplayName,
  getSelectedEntityKey,
} from './mention-entity-picker-helpers';
import type {
  MultiSelectSearchResult,
  PickerItem,
  SearchGroup,
  SearchResult,
} from './mention-entity-picker-helpers';
import {
  getDocumentMentionElement,
  getMentionNodeForResult,
  getSessionMentionKeys,
  restoreSessionMentions,
  toggleSessionMention,
} from './mention-entity-selection';
import type { SessionMentions } from './mention-entity-selection';
import { ELEMENT_SKILL_INPUT, getHasSkillPicker } from './skill-element-plugin';
import { ChatIntegrationsMenu } from '../../ChatEditor/ChatEditorIntegrations';
import { MENTION_TRIGGER_PREVIOUS_CHAR_PATTERN } from '../../ChatEditor/ChatEditorMention/mention-trigger';
import { MentionElement } from '../../ChatEditor/ChatEditorMention/MentionElement';
import type {
  AlineAiMentionElement,
  EmailMentionElement,
  IntakeFormMentionElement,
  TMentionElement,
} from '../../ChatEditor/ChatEditorMention/utils';
import type { PickerKeyboardEvent } from '../../ChatEditor/ChatEditorTriggerPicker';
import {
  getWasTriggerTyped,
  keepPickerFocus,
  OPENED_WITHOUT_TYPING,
  stopPickerKeyboardEvent,
  TriggerPickerInput,
  useTriggerPicker,
} from '../../ChatEditor/ChatEditorTriggerPicker';
import { SKILL_PICKER_VIEWS } from '../../ChatEditor/ChatEditorTriggerPicker/trigger-origin';
import type { SkillPickerView } from '../../ChatEditor/ChatEditorTriggerPicker/trigger-origin';
import { MAX_CHAT_UPLOAD_FILES } from '../../ChatEditor/utils';
import type { MentionEntitiesConfig } from '../types';

const ALINE_AI_OPTION_KEY = 'aline-ai';
const ALINE_AI_OPTION_LABEL = 'Aline AI';
const INTAKE_FORM_OPTION_LABEL = 'Intake Form';
export const ALINE_MARKDOWN_MENTION_MENU_ATTR = 'data-aline-markdown-mention-menu';
const MENTION_MENU_PROPS = { [ALINE_MARKDOWN_MENTION_MENU_ATTR]: true };
const SKELETON_ROW_COUNT = 5;

const CATEGORY_LABELS: Record<Apollo.MentionEntitySearchType, string> = {
  [Apollo.MentionEntitySearchType.Document]: 'Documents',
  [Apollo.MentionEntitySearchType.Playbook]: 'Playbooks',
  [Apollo.MentionEntitySearchType.Report]: 'Reports',
  [Apollo.MentionEntitySearchType.User]: 'Users',
  [Apollo.MentionEntitySearchType.Workflow]: 'Workflows',
};

const ENTITY_TYPE_ICONS: Record<Apollo.MentionEntitySearchType, LucideIcon> = {
  [Apollo.MentionEntitySearchType.Document]: FileText,
  [Apollo.MentionEntitySearchType.Playbook]: BookOpenText,
  [Apollo.MentionEntitySearchType.Report]: ChartColumnBig,
  [Apollo.MentionEntitySearchType.User]: UserRound,
  [Apollo.MentionEntitySearchType.Workflow]: Workflow,
};

const RESULT_TYPES: Record<SearchResult['__typename'], Apollo.MentionEntitySearchType> = {
  Document: Apollo.MentionEntitySearchType.Document,
  Playbook: Apollo.MentionEntitySearchType.Playbook,
  Report: Apollo.MentionEntitySearchType.Report,
  User: Apollo.MentionEntitySearchType.User,
  Workflow: Apollo.MentionEntitySearchType.Workflow,
};

interface PickerRowHandlers {
  highlightedItemKey: string | null;
  onHighlight: (key: string) => void;
  onSelect: (item: PickerItem) => void;
  onToggleEntity: (result: MultiSelectSearchResult) => void;
  selectedEntityKeys: Set<string>;
}

interface PickerItemsProps extends PickerRowHandlers {
  items: PickerItem[];
  showHeaders?: boolean;
}

export const MentionEntitiesContext = createContext<MentionEntitiesConfig | null>(null);

export function useMentionEntitiesContext(): MentionEntitiesConfig {
  const ctx = useContext(MentionEntitiesContext);
  if (!ctx)
    throw new Error('useMentionEntitiesContext must be used within MentionEntitiesContext.');
  return ctx;
}

export const MentionElementPlugin =
  toPlatePlugin(BaseMentionElementPlugin).withComponent(MentionElement);

export const MentionEntityKit = [
  MentionPlugin.configure({
    options: {
      triggerPreviousCharPattern: MENTION_TRIGGER_PREVIOUS_CHAR_PATTERN,
    },
  }).withComponent(MentionElement),
  MentionInputPlugin.configure({
    node: {
      isInline: true,
      isVoid: false,
    },
  }).withComponent(MentionEntityInputElement),
];

const useStyles = makeStyles()(theme => ({
  categoryItemContainer: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    minWidth: 1,
    width: '100%',
  },
  disabledItem: {
    '&:hover': {
      backgroundColor: 'transparent',
    },
    color: theme.palette.text.disabled,
    cursor: 'default',
  },
  drilldownEmpty: {
    color: theme.palette.text.secondary,
    fontSize: 14,
    padding: theme.spacing(1.5, 2),
    textAlign: 'center',
  },
  drilldownHeader: {
    gap: theme.spacing(0.75),
  },
  drilldownHeaderButton: {
    color: 'inherit',
    height: 20,
    padding: 0,
    width: 20,
  },
  emailTitleDisabled: {
    color: theme.palette.text.disabled,
  },
  emptyState: {
    padding: theme.spacing(0.5, 1),
  },
  groupHeader: {
    alignItems: 'center',
    backgroundColor: theme.palette.grey[100],
    boxSizing: 'border-box',
    color: theme.palette.text.secondary,
    display: 'flex',
    fontSize: 12,
    fontWeight: 700,
    padding: theme.spacing(0.25, 1),
    position: 'sticky',
    textTransform: 'uppercase',
    top: 0,
    width: '100%',
    zIndex: 1,
  },
  highlightedItem: {
    backgroundColor: theme.appColors.selectedBackground,
  },
  item: {
    '&:hover': {
      backgroundColor: theme.appColors.hoverBackground,
    },
    borderRadius: 4,
    cursor: 'pointer',
    margin: 0,
    padding: theme.spacing(0.25, 1),
  },
  itemCheckbox: {
    flexShrink: 0,
    padding: 0,
  },
  itemContainer: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
    minWidth: 0,
  },
  itemText: {
    display: 'flex',
    minWidth: 0,
  },
  mentionIcon: {
    alignItems: 'center',
    backgroundColor: 'rgba(20, 110, 232, 0.12)',
    borderRadius: '50%',
    color: theme.appColors.primary,
    display: 'flex',
    flexShrink: 0,
    fontSize: 16,
    fontWeight: 700,
    height: 20,
    justifyContent: 'center',
    width: 20,
  },
  plusIcon: {
    alignItems: 'center',
    backgroundColor: theme.appColors.primary,
    borderRadius: '50%',
    color: theme.palette.common.white,
    display: 'flex',
    flexShrink: 0,
    height: alineAvatarSizes.xs.size,
    justifyContent: 'center',
    width: alineAvatarSizes.xs.size,
  },
  plusIconDisabled: {
    backgroundColor: theme.palette.action.disabledBackground,
    color: theme.palette.text.disabled,
  },
  root: {
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    maxHeight: 'min(340px, calc(100vh - 24px))',
    maxWidth: 'calc(100vw - 16px)',
    overflow: 'hidden',
    padding: 0,
    width: 340,
    zIndex: theme.zIndex.modal - 1,
  },
  // A row's icon takes its text's color, so the two read as one item.
  rowIcon: {
    alignItems: 'center',
    color: 'inherit',
    display: 'flex',
    flexShrink: 0,
    height: 20,
    justifyContent: 'center',
    width: 20,
  },
  scrollBody: {
    flex: '1 1 auto',
    minHeight: 0,
    overflowY: 'auto',
  },
  section: {
    width: '100%',
  },
  selectableItem: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(0.75),
  },
  selectableItemContent: {
    flex: 1,
    minWidth: 0,
  },
  skeletonText: {
    flex: 1,
    minWidth: 0,
  },
  title: {
    fontSize: 14,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  uploadButton: {
    '&:hover': { backgroundColor: theme.appColors.hoverBackground },
    borderRadius: 4,
    color: theme.palette.text.primary,
    display: 'flex',
    fontSize: 14,
    gap: theme.spacing(1),
    justifyContent: 'flex-start',
    padding: theme.spacing(0.5, 1),
    width: '100%',
  },
}));

export function MentionEntityInputElement(props: PlateElementProps<TElement>): JSX.Element {
  const editor = useEditorRef();
  const { classes } = useStyles();
  const config = useMentionEntitiesContext();
  const { element } = props;
  const [uploadOpen, setUploadOpen] = useState(false);
  const enabledTypes = config.enabledTypes ?? DEFAULT_ENABLED_TYPES;
  const [highlightedItemKey, setHighlightedItemKey] = useState<string | null>(null);
  const [activeType, setActiveType] = useState<Apollo.MentionEntitySearchType | null>(null);
  const allowSkills = getHasSkillPicker(editor);
  const sessionMentionsRef = useRef<SessionMentions>(new Map());
  const generatedSessionId = useId();
  const sessionId =
    typeof element.contextSessionId === 'string' ? element.contextSessionId : generatedSessionId;
  const [selectionGeneration, setSelectionGeneration] = useState(0);

  /**
   * The upload picker is a portaled dialog, so its clicks and keys land outside the popper.
   * Suspending the picker keeps the mention input mounted while an upload is in progress,
   * and the dialog with it.
   */
  const picker = useTriggerPicker({
    element,
    onEmptyDelete: handleEmptyDelete,
    onNavigationKeyDown: handlePickerNavigationKeyDown,
    suspended: uploadOpen,
    trigger: '@',
  });
  const { focusEditable, noteChoiceMade, query, removeTriggerAndRestoreCaret } = picker;

  const [debouncedQuery] = useDebounce(query, 250);
  const queryInput = getSearchMentionEntitiesInput({
    activeType,
    documentId: config.documentId,
    enabledTypes,
    query: debouncedQuery,
    quickTypes: config.quickTypes,
    workflowId: config.workflowId,
  });
  const { data, error, fetchMore, loading } = Apollo.useSearchMentionEntitiesQuery({
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
    variables: { input: queryInput },
  });
  const groups = useMemo(
    () => filterUserResults(data?.searchMentionEntities.groups ?? [], config.filterUser),
    [config.filterUser, data]
  );
  const items = useMemo(
    () =>
      getPickerItems({
        activeType,
        allowAlineAiMention: Boolean(config.allowAlineAiMention),
        allowEmailMentions: Boolean(config.allowEmailMentions),
        allowFileUpload: Boolean(config.allowFileUpload),
        allowIntakeFormMention: Boolean(config.allowIntakeFormMention),
        allowSkills,
        enabledTypes,
        groups,
        query,
        quickTypes: config.quickTypes,
      }),
    [
      activeType,
      allowSkills,
      config.allowAlineAiMention,
      config.allowEmailMentions,
      config.allowFileUpload,
      config.allowIntakeFormMention,
      config.quickTypes,
      enabledTypes,
      groups,
      query,
    ]
  );
  const optionKeys = useMemo(
    () => items.filter(item => !getIsPickerItemDisabled(item)).map(item => item.key),
    [items]
  );

  // The highlight falls back to the first row whenever its own row leaves the list.
  const validHighlightedKey =
    highlightedItemKey && optionKeys.includes(highlightedItemKey)
      ? highlightedItemKey
      : (optionKeys.at(0) ?? null);
  if (validHighlightedKey !== highlightedItemKey) setHighlightedItemKey(validHighlightedKey);

  const activeGroup = activeType ? (groups.find(group => group.type === activeType) ?? null) : null;
  const selectedEntityKeys = useEditorSelector(
    editor => getSessionMentionKeys(editor, sessionMentionsRef.current),
    [selectionGeneration],
    { equalityFn: getAreSetsEqual }
  );
  const scrollableTargetId = `mention-entity-scroll-${activeType ?? (query.trim() ? 'preview' : 'empty')}`;

  // Activity releases refs while hidden, then rebuilds them from this session's chips.
  useEffect(() => {
    const sessionMentions = sessionMentionsRef.current;
    restoreSessionMentions({ editor, sessionId, sessionMentions });
    setSelectionGeneration(value => value + 1);
    return () => {
      for (const pathRef of sessionMentions.values()) pathRef.unref();
      sessionMentions.clear();
    };
  }, [editor, sessionId]);

  /** Swap the trigger for these chips, each followed by a space, and hand focus back. */
  const insertMentionElements = useCallback(
    (mentionNodes: TMentionElement[]) => {
      const nodes = mentionNodes.flatMap(mentionNode => [mentionNode, { text: ' ' }]);
      if (!nodes.length) return;

      removeTriggerAndRestoreCaret();
      editor.tf.insertNodes(nodes, { select: true });
      focusEditable();
    },
    [editor, focusEditable, removeTriggerAndRestoreCaret]
  );

  const toggleEntitySelection = useCallback(
    (result: MultiSelectSearchResult) => {
      noteChoiceMade();
      toggleSessionMention({
        editor,
        inputElement: element,
        result,
        sessionId,
        sessionMentions: sessionMentionsRef.current,
      });
    },
    [editor, element, noteChoiceMade, sessionId]
  );

  const openDrilldown = useCallback((type: Apollo.MentionEntitySearchType) => {
    setActiveType(type);
    setHighlightedItemKey(null);
  }, []);

  const closeDrilldown = useCallback(() => {
    setActiveType(null);
    setHighlightedItemKey(null);
  }, []);

  /** Swap this picker for the `/` skill picker, which can step back here with the same query. */
  const openSkillPicker = useCallback(
    (initialView: SkillPickerView) => {
      removeTriggerAndRestoreCaret();
      editor.tf.insertNodes({
        children: [{ text: '' }],
        ...OPENED_WITHOUT_TYPING,
        contextOrigin: {
          initialView,
          query,
          sessionId,
          typed: getWasTriggerTyped(element),
        },
        type: ELEMENT_SKILL_INPUT,
      });
    },
    [editor, element, query, removeTriggerAndRestoreCaret, sessionId]
  );
  const openNewSkill = useCallback(
    () => openSkillPicker(SKILL_PICKER_VIEWS.NewSkill),
    [openSkillPicker]
  );

  const selectItem = useCallback(
    (item: PickerItem) => {
      switch (item.kind) {
        case 'aline-ai':
          insertMentionElements([
            {
              children: [{ text: '' }],
              mentionTypeName: 'aline-ai',
              type: mentionType,
              value: ALINE_AI_OPTION_KEY,
            } satisfies AlineAiMentionElement,
          ]);
          return;
        case 'documents-empty':
          return;
        case 'entity': {
          const selectableResult = getMultiSelectResultForItem(item);
          if (selectableResult) toggleEntitySelection(selectableResult);
          else insertMentionElements([getMentionNodeForResult(item.result)]);
          return;
        }
        case 'email':
          if (!item.disabled)
            insertMentionElements([
              {
                children: [{ text: '' }],
                email: item.email,
                mentionTypeName: 'email',
                type: mentionType,
                value: item.email,
              } satisfies EmailMentionElement,
            ]);
          return;
        case 'intake-form':
          insertMentionElements([
            {
              children: [{ text: '' }],
              mentionTypeName: 'intake-form',
              type: mentionType,
              value: INTAKE_FORM_OPTION_LABEL,
            } satisfies IntakeFormMentionElement,
          ]);
          return;
        case 'skills':
          openSkillPicker(SKILL_PICKER_VIEWS.List);
          return;
        case 'category':
        case 'view-all':
          openDrilldown(item.type);
      }
    },
    [insertMentionElements, openDrilldown, openSkillPicker, toggleEntitySelection]
  );

  const fetchMoreResults = useCallback(async () => {
    if (!activeGroup?.pageInfo.nextCursor || loading) return;

    await fetchMore({
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev;

        return {
          ...fetchMoreResult,
          searchMentionEntities: {
            ...fetchMoreResult.searchMentionEntities,
            groups: prev.searchMentionEntities.groups.map(group => {
              const nextGroup = fetchMoreResult.searchMentionEntities.groups.find(
                resultGroup => resultGroup.type === group.type
              );
              if (!nextGroup) return group;

              const existingIds = new Set(group.results.map(result => result.id));
              const newResults = nextGroup.results.filter(result => !existingIds.has(result.id));

              return {
                ...nextGroup,
                results: [...group.results, ...newResults],
              };
            }),
          },
        };
      },
      variables: {
        input: {
          ...queryInput,
          cursor: getMentionEntitySearchCursorInput(activeGroup.pageInfo.nextCursor),
        },
      },
    });
  }, [activeGroup?.pageInfo.nextCursor, fetchMore, loading, queryInput]);

  const openUpload = useCallback(() => setUploadOpen(true), []);

  /**
   * Declared after the data it reads, and hoisted back into the picker options above. The
   * hook reads it through a ref, so each render's copy is the one that runs.
   */
  function handlePickerNavigationKeyDown(event: PickerKeyboardEvent): boolean {
    switch (event.key) {
      case 'ArrowLeft':
        if (!activeType) return false;
        stopPickerKeyboardEvent(event);
        closeDrilldown();
        return true;
      case 'ArrowDown':
      case 'ArrowUp':
        stopPickerKeyboardEvent(event);
        moveHighlight(event.key === 'ArrowDown' ? 1 : -1);
        return true;
      case 'Enter':
      case 'Tab':
      case 'ArrowRight': {
        const highlighted = items.find(item => item.key === highlightedItemKey);
        if (!highlighted) return false;
        stopPickerKeyboardEvent(event);
        selectItem(highlighted);
        return true;
      }
    }
    return false;
  }

  function moveHighlight(step: number) {
    const count = Math.max(optionKeys.length, 1);
    const current = highlightedItemKey ? optionKeys.indexOf(highlightedItemKey) : -1;
    setHighlightedItemKey(optionKeys.at((current + step + count) % count) ?? null);
  }

  // An empty Backspace inside a category steps back to every category, like ArrowLeft.
  function handleEmptyDelete(): boolean {
    if (!activeType) return false;

    closeDrilldown();
    return true;
  }

  return (
    <TriggerPickerInput
      ariaLabel="Search mentions"
      dialog={
        config.allowFileUpload && (
          <UploadFilesPicker
            domContainer={FileStackDomContainer.ChatContext}
            open={uploadOpen}
            pickerOpts={{
              accept: [...aiChatAcceptedMimeTypes],
              afterUploadDone: data => {
                setUploadOpen(false);
                insertMentionElements(
                  (data.data?.createFiles ?? []).map(file => getDocumentMentionElement(file))
                );
              },
              maxFiles: MAX_CHAT_UPLOAD_FILES,
              onClose: () => setUploadOpen(false),
              props: {
                documentVariant: Apollo.DocumentVariant.Document,
                isSigned: false,
              },
            }}
          />
        )
      }
      elementProps={props}
      menu={
        <div className={classes.scrollBody} id={scrollableTargetId}>
          {(!activeType || activeType === Apollo.MentionEntitySearchType.Document) && (
            <MentionAddActions
              allowFileUpload={Boolean(config.allowFileUpload)}
              allowSkills={allowSkills}
              onNewSkill={openNewSkill}
              onUpload={openUpload}
            />
          )}
          {activeType && <DrilldownHeader type={activeType} onBack={closeDrilldown} />}
          <MentionResults
            activeGroup={activeGroup}
            activeType={activeType}
            error={Boolean(error)}
            highlightedItemKey={highlightedItemKey}
            items={items}
            loading={loading}
            query={query}
            scrollableTargetId={scrollableTargetId}
            selectedEntityKeys={selectedEntityKeys}
            onFetchMore={fetchMoreResults}
            onHighlight={setHighlightedItemKey}
            onSelect={selectItem}
            onToggleEntity={toggleEntitySelection}
          />
          {!activeType && config.allowIntegrations && (
            <div className={classes.section}>
              <div className={classes.groupHeader}>Integrations</div>
              <ChatIntegrationsMenu query={query} onClose={removeTriggerAndRestoreCaret} />
            </div>
          )}
        </div>
      }
      menuClassName={classes.root}
      menuProps={MENTION_MENU_PROPS}
      picker={picker}
      trigger="@"
    />
  );
}

function MentionAddActions({
  allowFileUpload,
  allowSkills,
  onNewSkill,
  onUpload,
}: {
  allowFileUpload: boolean;
  allowSkills: boolean;
  onNewSkill: () => void;
  onUpload: () => void;
}): JSX.Element | null {
  const { classes } = useStyles();
  if (!allowFileUpload && !allowSkills) return null;

  return (
    <div className={classes.section}>
      <div className={classes.groupHeader}>Add</div>
      {allowFileUpload && (
        <AddActionButton icon={<Upload size={18} />} label="Upload document" onClick={onUpload} />
      )}
      {allowSkills && (
        <AddActionButton icon={<Plus size={18} />} label="New skill" onClick={onNewSkill} />
      )}
    </div>
  );
}

function MentionResults({
  activeGroup,
  activeType,
  error,
  loading,
  onFetchMore,
  query,
  scrollableTargetId,
  ...itemsProps
}: PickerItemsProps & {
  activeGroup: SearchGroup | null;
  activeType: Apollo.MentionEntitySearchType | null;
  error: boolean;
  loading: boolean;
  onFetchMore: () => Promise<void>;
  query: string;
  scrollableTargetId: string;
}) {
  const { classes } = useStyles();

  if (!itemsProps.items.length) {
    if (loading) return <SkeletonRows />;
    if (activeType && !error) return <DrilldownEmptyState type={activeType} />;

    return (
      <Typography className={classes.emptyState} color="textSecondary" variant="body2">
        {getEmptyResultsMessage({ error, query })}
      </Typography>
    );
  }
  if (!activeGroup) return <PickerItems {...itemsProps} />;

  return (
    <InfiniteScroll
      dataLength={activeGroup.results.length}
      hasMore={activeGroup.pageInfo.hasMore}
      loader={<LoadingRow />}
      next={onFetchMore}
      scrollableTarget={scrollableTargetId}
    >
      <PickerItems {...itemsProps} showHeaders={false} />
    </InfiniteScroll>
  );
}

function SkeletonRows() {
  const { classes } = useStyles();

  return (
    <>
      {Array.from({ length: SKELETON_ROW_COUNT }, (_, index) => (
        <div key={index} className={classes.item}>
          <div className={classes.itemContainer}>
            <Skeleton height={20} variant="rectangular" width={20} />
            <Skeleton className={classes.skeletonText} height={18} variant="text" />
          </div>
        </div>
      ))}
    </>
  );
}

function DrilldownEmptyState({ type }: { type: Apollo.MentionEntitySearchType }) {
  const { classes } = useStyles();

  return (
    <div className={classes.drilldownEmpty}>No matching {CATEGORY_LABELS[type].toLowerCase()}</div>
  );
}

function DrilldownHeader({
  onBack,
  type,
}: {
  onBack: () => void;
  type: Apollo.MentionEntitySearchType;
}) {
  const { classes, cx } = useStyles();

  return (
    <div className={cx(classes.groupHeader, classes.drilldownHeader)}>
      <IconButton
        aria-label="Back to all mention categories"
        className={classes.drilldownHeaderButton}
        size="small"
        onClick={event => {
          event.stopPropagation();
          onBack();
        }}
        onMouseDown={keepPickerFocus}
      >
        <ArrowLeft size={14} />
      </IconButton>
      {CATEGORY_LABELS[type]}
    </div>
  );
}

function PickerItems({ items, showHeaders = true, ...rowProps }: PickerItemsProps) {
  const { classes } = useStyles();
  const sections = groupItemsByHeader(items);

  return (
    <>
      {sections.map((section, index) => (
        /**
         * Each section is its own sticky container so only one header pins to the top
         * at a time: as a section scrolls away its header leaves with it and the next
         * section's header takes over, instead of every sticky header stacking.
         */
        <div key={section.header ?? `section-${index}`} className={classes.section}>
          {showHeaders && section.header && (
            <div className={classes.groupHeader}>{section.header}</div>
          )}
          {section.items.map(item => (
            <PickerItemRow key={item.key} item={item} {...rowProps} />
          ))}
        </div>
      ))}
    </>
  );
}

function PickerItemRow({
  highlightedItemKey,
  item,
  onHighlight,
  onSelect,
  onToggleEntity,
  selectedEntityKeys,
}: PickerRowHandlers & { item: PickerItem }) {
  const { classes, cx } = useStyles();
  const selectableResult = getMultiSelectResultForItem(item);
  const disabled = getIsPickerItemDisabled(item);

  return (
    <div
      className={cx(
        classes.item,
        disabled && classes.disabledItem,
        !disabled && item.key === highlightedItemKey && classes.highlightedItem,
        selectableResult && classes.selectableItem
      )}
      onClick={event => {
        event.stopPropagation();
        if (!disabled) onSelect(item);
      }}
      onMouseDown={keepPickerFocus}
      onMouseEnter={() => onHighlight(item.key)}
    >
      <PickerItemRowContent
        item={item}
        selectableResult={selectableResult}
        selectedEntityKeys={selectedEntityKeys}
        onToggleEntity={onToggleEntity}
      />
    </div>
  );
}

function PickerItemRowContent({
  item,
  onToggleEntity,
  selectableResult,
  selectedEntityKeys,
}: {
  item: PickerItem;
  onToggleEntity: (result: MultiSelectSearchResult) => void;
  selectableResult: MultiSelectSearchResult | null;
  selectedEntityKeys: Set<string>;
}) {
  const { classes } = useStyles();
  if (!selectableResult) return <MentionEntityRow item={item} />;

  return (
    <>
      <AlineCheckbox
        checked={selectedEntityKeys.has(getSelectedEntityKey(selectableResult))}
        className={classes.itemCheckbox}
        size="small"
        slotProps={{
          htmlInput: {
            'aria-label': `Select ${getSearchResultDisplayName(selectableResult)}`,
          },
        }}
        onChange={() => onToggleEntity(selectableResult)}
        onClick={event => event.stopPropagation()}
      />
      <div className={classes.selectableItemContent}>
        <MentionEntityRow item={item} />
      </div>
    </>
  );
}

function MentionEntityRow({ item }: { item: PickerItem }): JSX.Element {
  const { classes, cx } = useStyles();

  switch (item.kind) {
    case 'aline-ai':
      return (
        <ItemRow
          icon={
            <div className={classes.mentionIcon}>
              <Sparkle size={14} strokeWidth={2} />
            </div>
          }
          title={ALINE_AI_OPTION_LABEL}
        />
      );
    case 'category':
      return (
        <ChevronRow icon={<EntityTypeIcon type={item.type} />} label={CATEGORY_LABELS[item.type]} />
      );
    case 'documents-empty':
      return <DocumentsEmptyRow />;
    case 'email':
      return (
        <ItemRow
          icon={
            <span className={cx(classes.plusIcon, item.disabled && classes.plusIconDisabled)}>
              <Plus size={14} strokeWidth={2.5} />
            </span>
          }
          title={item.email}
          titleClassName={item.disabled && classes.emailTitleDisabled}
        />
      );
    case 'entity':
      return (
        <ItemRow
          icon={<SearchResultIcon result={item.result} />}
          title={getSearchResultDisplayName(item.result)}
        />
      );
    case 'intake-form':
      return (
        <ItemRow
          icon={
            <span className={classes.rowIcon}>
              <ClipboardList size={15} />
            </span>
          }
          title={INTAKE_FORM_OPTION_LABEL}
        />
      );
    case 'skills':
      return (
        <ChevronRow
          icon={
            <span className={classes.rowIcon}>
              <Sparkles size={16} strokeWidth={2} />
            </span>
          }
          label="Skills"
        />
      );
    case 'view-all':
      return (
        <ChevronRow
          icon={<EntityTypeIcon type={item.type} />}
          label={`View all ${CATEGORY_LABELS[item.type].toLowerCase()}...`}
        />
      );
  }
}

function ItemRow({
  icon,
  title,
  titleClassName,
}: {
  icon: React.ReactNode;
  title: string;
  titleClassName?: string | false;
}) {
  const { classes, cx } = useStyles();

  return (
    <div className={classes.itemContainer}>
      {icon}
      <div className={classes.itemText}>
        <Typography className={cx(classes.title, titleClassName)}>{title}</Typography>
      </div>
    </div>
  );
}

// A row that drills into a category or hands off to another picker.
function ChevronRow({ icon, label }: { icon: React.ReactNode; label: string }) {
  const { classes } = useStyles();

  return (
    <div className={classes.categoryItemContainer}>
      <div className={classes.itemContainer}>
        {icon}
        <Typography className={classes.title}>{label}</Typography>
      </div>
      <ChevronRight className={classes.rowIcon} size={16} />
    </div>
  );
}

function AddActionButton({
  icon,
  label,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
}): JSX.Element {
  const { classes } = useStyles();
  return (
    <ButtonBase
      className={classes.uploadButton}
      onClick={event => {
        event.stopPropagation();
        onClick();
      }}
      onMouseDown={keepPickerFocus}
    >
      <span className={classes.rowIcon}>{icon}</span>
      {label}
    </ButtonBase>
  );
}

function DocumentsEmptyRow(): JSX.Element {
  const { classes } = useStyles();

  return (
    <div className={classes.itemContainer} style={{ justifyContent: 'center' }}>
      <Typography align="center" className={classes.title} color="textSecondary" variant="body2">
        No documents yet. Upload to get started.
      </Typography>
    </div>
  );
}

function SearchResultIcon({ result }: { result: SearchResult }) {
  if (result.__typename === 'Document')
    return <StorableIcon iconProps={{ height: 18, width: 18 }} storable={result} />;
  if (result.__typename === 'User')
    return <AlineAvatar avatarInfo={result} styles={{ size: 'xs' }} />;
  return <EntityTypeIcon type={RESULT_TYPES[result.__typename]} />;
}

function EntityTypeIcon({ type }: { type: Apollo.MentionEntitySearchType }) {
  const { classes } = useStyles();
  const Icon = ENTITY_TYPE_ICONS[type];

  return (
    <span className={classes.rowIcon}>
      <Icon size={15} />
    </span>
  );
}

function LoadingRow() {
  const { classes } = useStyles();
  return (
    <Typography className={classes.emptyState} color="textSecondary" variant="body2">
      Loading more...
    </Typography>
  );
}

function groupItemsByHeader(items: PickerItem[]) {
  const sections: { header: string | null; items: PickerItem[] }[] = [];

  for (const item of items) {
    const current = sections.at(-1);
    const header = getHeaderForItem(item);
    // Header-less items, such as "view all", stay grouped with the section above them.
    if (header && current?.header !== header) sections.push({ header, items: [item] });
    else if (current) current.items.push(item);
    else sections.push({ header: null, items: [item] });
  }

  return sections;
}

function getHeaderForItem(item: PickerItem) {
  switch (item.kind) {
    case 'aline-ai':
    case 'email':
      return CATEGORY_LABELS[Apollo.MentionEntitySearchType.User];
    case 'documents-empty':
      return CATEGORY_LABELS[Apollo.MentionEntitySearchType.Document];
    case 'entity':
      return CATEGORY_LABELS[RESULT_TYPES[item.result.__typename]];
    case 'category':
    case 'intake-form':
    case 'skills':
      return 'More Mentions';
    case 'view-all':
      return null;
  }
}

function getIsPickerItemDisabled(item: PickerItem) {
  return item.kind === 'documents-empty' || (item.kind === 'email' && item.disabled);
}

function getEmptyResultsMessage({ error, query }: { error: boolean; query: string }) {
  if (error) return 'Could not load mentions. Try again.';
  if (query.trim()) return 'No mentions match your search.';
  return 'No mentions available.';
}

function getAreSetsEqual(a: Set<string>, b: Set<string>) {
  return a.size === b.size && [...a].every(key => b.has(key));
}
